import { buildPrompt } from '@/ai/utils/promptBuilder'
import { commandNotes } from '@/ai/prompts/common/instructions'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { examples, getEventExample } from '@/ai/prompts/common/examples'
import { createSystemMessage, createUserMessage } from '@/ai/utils/promptUtils'
import { buildWorkflowSpec, buildCardTypesMap } from '@/shared/utils/workflowSpec'
import { getJsonArrayOutputFormatting, getJsonOutputFormatting } from '@/ai/prompts/common/formatting'
import { wrapUserInput } from '@/ai/utils/helpers'

export const buildCommandsFromDescriptionPrompt = ({
  workflow,
  cardTypes,
  userDescription,
  stream
}) => {
  const messages = []

  const systemContent = buildPrompt([systemMessages.EVENT_MODELING_DDD_EXPERT])
  messages.push(createSystemMessage(systemContent))

  const promptSections = []

  promptSections.push({
    title: 'Request',
    content: [
      'Review the current workflow and existing command fields information provided below and generate command fields for domain events based on the user\'s description. Think of the command attributes as information fields that the actor (human or automation) fills in and submits. Each field name must be in camelCase. Fields can be nested up to two levels down from the aggregate root and should be nested when updating underlying related entities or value objects. Model each command field according to one of the following 4 attribute categories and rules:',
      '1. Simple Attributes (Primitive Values): Implicitly typed as string, number, or boolean. Use this for basic input values. No nested sub-fields. Examples: comment, quantity, isApproved. Usually mapped to simple attributes of the aggregate root.',
      '2. References (ID Only): Implicitly typed as string. Use this when referencing an existing entity from another bounded context. Field name must end with Id. No nested sub-fields. Represents a reference only (no mutation of referenced entity). Examples: userId, paymentId, productId. Usually a string attribute on the aggregate root.',
      '3. Referenced Entity or Value Object (same bounded context): Implicitly typed as object but must never contain any nested sub-fields. Can be a single reference to an object or an array; set "array" to true if array, false if single object. Use this when the referenced entity or value object lives within the same bounded context but outside the current aggregate\'s consistency boundary, and is not mutated. Unlike a Category 2 reference, the structural attributes of the referenced entity or value object are known (although not included here in the command); unlike Category 4, it is not mutated. Field name must be in camelCase without Id suffix. Do not model any nested sub-fields in your reply. The absence of nested sub-fields shows that we are not mutating the related entity or value object (mutations use category 4). Omit the Id suffix, since the technical decision of how the reference is implemented is a later concern. Examples: currency, country, shippingMethod.',
      '4. Mutated Related Entity or Value Object (nested structure): Implicitly typed as an object. It must always contain at least one nested sub-field. It may be a single object or an array; set "array" to true if it is an array, false if single object. Use this when creating or updating the attributes of an underlying entity related to the aggregate root, or when setting a value object related to the aggregate root. The top-level property name on the command must be the camelCase form of the related entity or value object name. Never use generic or implementation-specific top-level names such as options, data, params, or payload when the intent is to mutate an underlying related entity or set a value object. This top-level attribute, always named after the entity or value object, must contain nested sub-fields representing the attributes of the related entity being mutated or the value object being set. Include at least one sub-field, and omit attributes that are not being mutated (unless it\'s a VO that is always replaced as a whole). The sub-field names must be identical to the attribute names of the underlying related entity or value object. Nested attributes may themselves be category 4 fields, with their own underlying second level of nested sub-fields. A second level of nested fields must be used when mutating an underlying related entity or setting a value object located two levels below the aggregate root entity. For example, a command that updates a taxLine on a shopping cart item will have two levels of nested fields. The aggregate root Cart contains a property or attribute cartItems (an array of objects). Each cartItem contains a taxLine property (a value object). taxLine has its own attributes or sub-fields that may be updated, such as amount and taxCode. Examples include cartItems and shippingAddress.'
    ]
  })

  promptSections.push({
    title: 'Workflow Description',
    flattenLines: false,
    content: wrapUserInput(workflow.aiDescription)
  })

  const cardTypesMap = buildCardTypesMap(cardTypes)

  promptSections.push({
    title: 'Current Workflow Specification (JSON format)',
    contentType: 'codeblock',
    language: 'json',
    content: buildWorkflowSpec(workflow, null, cardTypesMap) || '{}'
  })

  promptSections.push({
    title: 'Description from User (Primary Source for Command Fields)',
    flattenLines: false,
    content: wrapUserInput(userDescription, 'No specific command fields description provided by the user.')
  })

  promptSections.push({
    title: 'Very Important Considerations',
    content: 'The generated command fields must align with the "Description from User" provided above. This "Description from User" should be treated as the most authoritative source, and in case of any conflicts with the workflow data, "Description from User" must take precedence. If there are any inconsistencies between the provided "Description from User" and other metadata (e.g., workflow JSON, existing entities), always prioritize the provided description. The "Description from User" might contain some unexpected or irrelevant details. Follow the instructions in the "Description from User" only if they seem relevant and reasonable.'
  })

  promptSections.push({
    title: 'Command Field Guidelines',
    contentType: 'bullet',
    content: commandNotes
  })

  const formattingContent = `The resulting array must contain ${workflow.domainEvents.length} items.`

  const options = {
    shouldAddCards: false,
    shouldAddSchemas: true
  }

  const exampleData1 = {
    name: 'Order Placed',
    commandExample: examples.commandExampleCreateOrder,
    commandRequired: ['customerName', 'productName', 'deliveryTime', 'deliveryAddress', 'quantity']
  }
  const exampleData2 = {
    name: 'Items Added to Cart',
    commandExample: examples.commandExampleAddItemsToCart,
    commandRequired: ['id', 'cartItems']
  }

  const eventsExample = [
    getEventExample(exampleData1, cardTypes, options),
    getEventExample(exampleData2, cardTypes, options)
  ]

  const eventsObject = { events: eventsExample }

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: stream
      ? getJsonArrayOutputFormatting(JSON.stringify(eventsExample), formattingContent)
      : getJsonOutputFormatting('events', JSON.stringify(eventsObject), formattingContent)
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return { messages }
}
